// Libraries
import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, effect, inject, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect, MatSelectChange, MatSelectTrigger } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addHours } from 'date-fns';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { debounceTime, distinctUntilChanged, first, forkJoin, from, Observable, Subject, switchMap, takeUntil } from 'rxjs';

// Services
import { FlowService } from 'src/app/services/api/flows/flow.service';
import { LabelProjectService } from 'src/app/services/api/labelProjects/labelProject.service';
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import { LabelProject } from 'src/app/interfaces/api/labelProject';
import { Model, PutModel } from 'src/app/interfaces/api/model';
import { Project, PutProject, Schedule } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { User } from 'src/app/interfaces/api/user';
import { COUNTRY_TIMEZONE_MAP, TIMEZONES } from 'src/app/interfaces/shared/timezones';

// Classes
import { Pop } from 'src/app/classes/shared/pop';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import { MatSelectFixDirective } from 'src/app/directives/matSelectFix.directive';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

// Components
import { MetricEditorComponent } from 'src/app/components/project-settings/metric-editor/metric-editor.component';
import { ButtonComponent, ConfirmComponent, DatePickerComponent, FileInputComponent } from 'ui-library';
import { ScheduleComponent } from '../../shared/schedule/schedule.component';

const DATAVIZ_TABS = ['dashboard', 'forge', 'maps', 'planning', 'events', 'actions', 'risks', 'timelapseGo', 'stream'];
const SETTING_TABS = ['info', 'resource', 'flow', 'models', 'users', 'privacy', 'tag', 'exportdb', 'kyd'];

const ALL_TAB_OPTIONS: { value: string; label: string }[] = [
    { value: 'dashboard', label: 'dashboard.title' },
    { value: 'forge', label: 'viewer.forge' },
    { value: 'maps', label: 'viewer.maps' },
    { value: 'planning', label: 'planning.title' },
    { value: 'events', label: 'events.title' },
    { value: 'actions', label: 'actions.title' },
    { value: 'risks', label: 'risks.title' },
    { value: 'timelapseGo', label: 'enlaps.title' },
    { value: 'stream', label: 'stream.title' },
    { value: 'info', label: 'settings.project.informations' },
    { value: 'resource', label: 'title.resources' },
    { value: 'flow', label: 'menu.workflows.title' },
    { value: 'models', label: 'settings.project.models' },
    { value: 'users', label: 'settings.project.userList' },
    { value: 'privacy', label: 'settings.project.privacy' },
    { value: 'tag', label: 'settings.project.editTag' },
    { value: 'exportdb', label: 'settings.project.database' },
    { value: 'kyd', label: 'settings.project.kyd_reports' },
];

@Component({
    selector: 'app-information',
    imports: [
        TranslateModule,
        CommonModule,
        FormsModule,
        ClipboardModule,
        TimeFormatPipe,
        DatePickerComponent,
        ButtonComponent,
        MatIconModule,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        MatTooltip,
        MatToolbar,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelTitle,
        MatExpansionPanelHeader,
        MatFormField,
        MatSlideToggleModule,
        NgxMaterialTimepickerModule,
        FileInputComponent,
        MatSelectFixDirective,
        MatButtonToggleModule,
        MatSelectTrigger,
        ScheduleComponent,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatPrefix
    ],
    standalone: true,
    templateUrl: './information.component.html',
    styleUrl: './information.component.scss'
})
export class InformationComponent implements OnDestroy, AfterViewInit {
    // Services
    private readonly auth: AuthService = inject(AuthService);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly projectService: ProjectService = inject(ProjectService);
    private readonly labelProjectService: LabelProjectService = inject(LabelProjectService);
    private readonly flowService: FlowService = inject(FlowService);
    private readonly uiTools: UiTool = inject(UiTool);
    private readonly dialog: MatDialog = inject(MatDialog);
    private readonly pop: Pop = inject(Pop);
    private readonly router: Router = inject(Router);
    private readonly modelService: ModelService = inject(ModelService);

    // Variables
    project!: Project;
    user!: User;
    isUserAdmin = false;
    projectForm: any;
    currentProject!: Project;
    transfer = null;
    trackers: Tracker[] = [];
    days: Array<{ name: string, value: string }> = [];
    schedule: any = {};
    pictureResult: any;
    logoResult: any;
    timezones: string[] = TIMEZONES;
    tabIndex: number = 0;
    loading: boolean = false;
    destroy$ = new Subject<void>();
    newEndDate: any;
    riskReportParams: string[] = ['description', 'zone', 'sequence'];
    selectedAIModules: string[] = [];
    selectedTabs: string[] = [];
    readonly tabOptions = ALL_TAB_OPTIONS;
    iaModels: string[] = ["llm", "crane", "worker", "risk", "action", "zone"];
    modalityOptions = [
        { value: 'sale', name: 'Vente', icon: 'shopping_cart' },
        { value: 'location', name: 'Location', icon: 'event_repeat' }
    ];
    modalityEditVisible: boolean = false;
    labelProjects: LabelProject[] = [];
    selectedIterations: { [key: string]: string } = {};
    streetSuggestions: any[] = [];
    streetInput$ = new Subject<string>();

    private animationFrameId: number | null = null;
    private initialState: any;
    private lastCheckTime = 0;
    private readonly CHECK_INTERVAL = 100;

    // Signals
    readonly userSignal: Signal<User> = this.auth.currentUser;
    readonly projectSignal: Signal<Project> = this.projectService.currentProjectSignal;
    readonly labelProjectsSignal: Signal<LabelProject[]> = this.labelProjectService.labelProjectsSignal;
    readonly isPrivileged: Signal<boolean> = this.auth.isPrivileged;
    private readonly changeDetectionTrigger: WritableSignal<number> = signal(0);

    // Computeds
    readonly selectedModality = computed(() =>
        this.modalityOptions.find(
            opt => opt.value === this.project?.modality
        )
    );

    readonly hasChanges = computed(() => {
        this.changeDetectionTrigger();

        if (!this.initialState) return false;

        const currentState = {
            project: this.project,
            schedule: this.schedule,
            selectedAIModules: this.selectedAIModules,
            selectedIterations: this.selectedIterations,
            pictureResult: this.pictureResult,
            logoResult: this.logoResult,
            reportParams: this.riskReportParams,
        };

        return JSON.stringify(currentState) !== JSON.stringify(this.initialState);
    });

    constructor() {
        this.isUserAdmin = this.userSignal().role?.name == "userAdmin" || this.isPrivileged();
        this.loading = true;
        this.streetInput$ = new Subject<string>();

        this.streetInput$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(query => from(this.getStreetSuggestions(query))),
            takeUntil(this.destroy$)
        ).subscribe(suggestions => {
            this.streetSuggestions = suggestions;
        });

        effect(() => {
            const project = this.projectSignal();
            const labelProjects = this.labelProjectsSignal();

            this.labelProjects = labelProjects;

            if (this.project?.id && this.project.id != project.id) {
                this.project = project;
                this.initSchedule();
            } else if (project) {
                this.project = project;
                this.initSchedule();
            }

            this.selectedTabs = [...(project.datavizTabs ?? []), ...(project.settingTabs ?? [])];

            this.selectedAIModules = [];

            if (project && !project.aiModules && labelProjects.length) {
                this.project.aiModules = {};

                labelProjects.forEach(labelProject => {
                    this.project.aiModules![labelProject.name] = { enabled: false, iteration: "" };
                });
            }

            if (this.project?.aiModules) {
                Object.keys(this.project.aiModules).forEach(module => {
                    let aiModule = (this.project.aiModules as any)[module];

                    if (aiModule?.enabled) {
                        this.selectedAIModules.push(module);
                        let labelProject = this.labelProjects.find(lp => lp.name == module);

                        if (labelProject?.iterations && labelProject.iterations.length > 1) {
                            this.selectedIterations[module] = aiModule.iteration || labelProject.iterations[0];
                        }
                    }
                });
            }

            this.newEndDate = this.project?.endDate;
            const r = this.project?.reportParams;

            this.riskReportParams = r
                ? (['description', 'zone', 'sequence'] as const).filter(k => r[k])
                : ['description', 'zone', 'sequence'];

            this.loading = false;
            this.pictureResult = null;
            this.logoResult = null;
            this.saveInitialState();
        });

        setTimeout(() => {
            this.setDays();
        }, 1000);

        this.labelProjectService.getLabelProjects({}).pipe(first()).subscribe(res => { });
    }

    // ================== Angular Lifecycle ================== //
    ngAfterViewInit() {
        this.checkChangesLoop();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }

    // ================== Initialization ================== //
    initSchedule() {
        if (!this.project.schedule) {
            this.project.schedule = {
                mon: { enable: true, times: [['00:00', '23:59']] },
                tue: { enable: true, times: [['00:00', '23:59']] },
                wed: { enable: true, times: [['00:00', '23:59']] },
                thu: { enable: true, times: [['00:00', '23:59']] },
                fri: { enable: true, times: [['00:00', '23:59']] },
                sat: { enable: false, times: [['00:00', '23:59']] },
                sun: { enable: false, times: [['00:00', '23:59']] },
            };
        }

        this.schedule = this.project.schedule;
    }

    setDays() {
        this.translate.get('date.monday').pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.days = [
                { name: this.translate.instant('date.monday'), value: 'mon' },
                { name: this.translate.instant('date.tuesday'), value: 'tue' },
                { name: this.translate.instant('date.wednesday'), value: 'wed' },
                { name: this.translate.instant('date.thursday'), value: 'thu' },
                { name: this.translate.instant('date.friday'), value: 'fri' },
                { name: this.translate.instant('date.saturday'), value: 'sat' },
                { name: this.translate.instant('date.sunday'), value: 'sun' },
            ]
        });
    }

    // ================== Address Management ================== //
    getStreetSuggestions(query: string): Promise<any[]> {
        if (!query || query.trim().length < 2) {
            return Promise.resolve([]);
        }

        if (!(window as any).google?.maps?.places) {
            return Promise.resolve([]);
        }

        const service = new (window as any).google.maps.places.AutocompleteService();

        return new Promise((resolve) => {
            service.getPlacePredictions({ input: query }, (predictions: any[]) => {
                resolve(predictions || []);
            });
        });
    }

    onSelectStreet(place: any) {
        if (!place.place_id) return;

        if (!(window as any).google?.maps?.places) return;

        const service = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
        service.getDetails({ placeId: place.place_id }, (placeDetails: any) => {
            if (placeDetails) {
                this.project.street = placeDetails.formatted_address;

                const components = placeDetails.address_components || [];
                const country = this.extractAddressComponent(components, 'country');

                this.project.country = country;
                this.project.city = this.extractAddressComponent(components, 'locality') ||
                    this.extractAddressComponent(components, 'administrative_area_level_1');
                this.project.zipCode = this.extractAddressComponent(components, 'postal_code');

                if (placeDetails.geometry?.location) {
                    this.project.lat = placeDetails.geometry.location.lat();
                    this.project.lon = placeDetails.geometry.location.lng();
                }

                if (country && COUNTRY_TIMEZONE_MAP[country]) {
                    this.project.timezone = COUNTRY_TIMEZONE_MAP[country];
                }

                this.streetSuggestions = [];
            }
        });
    }

    private extractAddressComponent(components: any[], type: string): string {
        const component = components.find(c => c.types.includes(type));
        return component ? component.long_name : '';
    }

    // ================== Project Management ================== //
    onTabsChange(tabs: string[]) {
        this.selectedTabs = tabs;
        this.project.datavizTabs = tabs.filter(t => DATAVIZ_TABS.includes(t));
        this.project.settingTabs = tabs.filter(t => SETTING_TABS.includes(t));
    }

    // Daily stream-time allowance is stored in seconds but edited in minutes.
    get streamDailyLimitMinutes(): number {
        return this.project?.streamDailyLimit ? Math.round(this.project.streamDailyLimit / 60) : 0;
    }
    set streamDailyLimitMinutes(minutes: number) {
        this.project.streamDailyLimit = Math.max(0, Math.round(minutes || 0)) * 60;
    }

    editProject() {
        if (!this.hasChanges() || this.loading) return;

        this.loading = true;

        let json: PutProject = {
            id: this.project.id,
            name: this.project.name,
            logo: this.project.logo,
            reference: this.project.reference,
            city: this.project.city,
            lat: this.project.lat,
            lon: this.project.lon,
            description: this.project.description,
            model: this.project.model,
            timezone: this.project.timezone,
            locale: this.project.locale,
            offlineDelay: this.project.offlineDelay,
            type: this.project.type,
            startingPage: this.project.startingPage,
            datavizTabs: this.project.datavizTabs,
            settingTabs: this.project.settingTabs,
            portNtrip: this.project.portNtrip,
            metricModels: this.project.metricModels,
            reportsEnabled: this.project.reportsEnabled,
            endDate: this.project.endDate,
            street: this.project.street,
            country: this.project.country,
            zipCode: this.project.zipCode,
        }

        this.transfer = null;

        if (this.isPrivileged()) {
            json.transfer = this.project.transfer;

            if (this.project.nelia)
                json.nelia = {
                    enabled: this.project.nelia.enabled,
                    realtime: this.project.nelia.realtime,
                    reportTime: this.project.nelia.reportTime || "18:00",
                    trackers: this.project.nelia.trackers,
                };

            if (this.project.enlaps)
                json.enlaps = {
                    url: this.project.enlaps.url,
                    enabled: this.project.enlaps.enabled
                };

            json.reportParams = {
                description: this.riskReportParams.includes('description'),
                zone: this.riskReportParams.includes('zone'),
                sequence: this.riskReportParams.includes('sequence'),
            };

            if (this.project?.services && this.currentProject?.services) {
                let servicesChanged = Object.keys(this.project.services).some((key: string) => {
                    return this.currentProject.services && (this.currentProject?.services as any)[key] !== (this.project.services as any)[key];
                });

                if (servicesChanged) {
                    json.services = this.project.services;
                }
            }

            json.aiModules = {};
            this.labelProjects.forEach(lp => json.aiModules![lp.name] = { enabled: false, iteration: "" });

            this.selectedAIModules.forEach((m: string) => {
                json.aiModules![m] = { enabled: true, iteration: this.selectedIterations[m] != '0' ? this.selectedIterations[m] || "" : "" };
            });
        }

        if (this.isUserAdmin) {
            json.geolocation = { accuracy: this.project.geolocation.accuracy, duration: this.project.geolocation.duration };
            json.pixelSize = this.project.pixelSize
            json.streamDailyLimit = this.project.streamDailyLimit ?? 0;
        }

        json.schedule = this.schedule;

        if (this.pictureResult === 'delete') {
            json.picture = '';
        } else if (this.pictureResult) {
            json.picture = this.pictureResult;
        }

        if (this.logoResult === 'delete') {
            json.logo = '';
        } else if (this.logoResult) {
            json.logo = this.logoResult;
        }

        this.projectService.setProject(json).pipe(first()).subscribe({
            next: (res: any) => {
                this.updateModelsOriginCoordinates();
                this.setCurrentProject(this.project.id, res);
                this.pictureResult = null;
                this.logoResult = null;
            },
            error: (error: any) => {
                this.loading = false;
                this.pop.toast(this.translate.instant('settings.project.save_error', { errorCode: error.status }), "danger")
            }
        });
    }

    setCurrentProject(projectId: string, saveResult?: any) {
        this.projectService.getProjectById(projectId).pipe(first()).subscribe((project: any) => {
            if (this.transfer)
                project.transfer = this.transfer;

            this.project = project;

            forkJoin([
                this.projectService.getStatus(project.database),
                this.projectService.getActivity({ databases: project.database })
            ]).pipe(first()).subscribe(([statusResult, activityResult]) => {
                this.project.status = statusResult[0];
                this.project.activity = activityResult;
                this.projectService.setCurrentProject(this.project);
                this.saveInitialState();
                this.loading = false;

                if (saveResult) {
                    if (saveResult.message)
                        this.pop.toast(this.translate.instant('settings.project.save_success'), "success", 4000, 'save');
                    else if (saveResult.warning)
                        this.pop.toast(this.translate.instant('settings.project.save_warning'), "warning", 4000, 'warning');
                }
            });
        })
    }

    deleteProject() {
        const dialog = this.dialog.open(ConfirmComponent, {
            data: {
                icon: 'delete',
                title: this.translate.instant('projects.deleteProject'),
                content: this.translate.instant('projects.confDelete') + ' "' + this.project.name + '" ? \n\r' + this.translate.instant('projects.confDelete2'),
                cancelIcon: 'close',
                cancelText: this.translate.instant('form.cancel'),
                okIcon: 'delete_forever',
                okText: this.translate.instant('form.delete'),
                okColor: 'danger',
                confirmationWord: 'form.delete'
            }
        });

        dialog.afterClosed().subscribe(res => {
            if (res.ok) {
                this.projectService.deleteProject(this.project.id).pipe(takeUntil(this.destroy$)).subscribe({
                    next: () => {
                        this.pop.toast(this.translate.instant('projects.project_deleted'), 'success', 5000, 'delete_forever');
                        this.projectService.deleteCurrentProject();
                        this.router.navigate(['/projects']);
                    },
                    error: (error: any) => {
                        this.pop.error(this.translate.instant('projects.project_delete_error', { errorCode: error.status }));
                    }
                })
            }
        })
    }

    // ================== Metric Management ================== //
    addMetric() {
        const dialogRef = this.dialog.open(MetricEditorComponent, {
            data: {},
            width: "600px"
        })

        dialogRef.afterClosed().subscribe(res => {
            if (!this.project.metricModels)
                this.project.metricModels = [];

            if (res && res.name) {
                this.project.metricModels.push(res)
                this.editProject();
            }
        })
    }

    exportMetric(index: any) {
        if (!this.project.metricModels || !this.project.metricModels[index]) return;

        const metrics: any = this.project.metricModels[index].payload;

        metrics.forEach((m: any) => {
            if (m.label == "startDate")
                m.label == "startDate" ? m.value = new Date().toISOString() : "";

            if (m.label == "endDate")
                m.value = addHours(new Date(), 1).toISOString();
        });

        const rows: string[] = [];
        const header = metrics.map((metric: any) => metric.label).join(";");
        rows.push(header);

        const dataRow = metrics.map((metric: any) => metric.value || "").join(";");
        rows.push(dataRow);

        const csvContent = rows.join("\n");
        const bom = "\uFEFF";
        const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", "metrics.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    editMetric(index: number) {
        const dialogRef = this.dialog.open(MetricEditorComponent, {
            width: "600px",
            data: this.project.metricModels ? { ...this.project.metricModels[index] } : {}
        })

        dialogRef.afterClosed().subscribe(res => {
            if (res && res.name && this.project.metricModels) {
                this.project.metricModels[index] = res;
                this.editProject();
            }
        })
    }

    deleteMetric(index: number) {
        if (this.project.metricModels) {
            this.project.metricModels.splice(index, 1);
            this.editProject();
        }
    }

    // ================== Schedule Management ================== //
    onScheduleChange(schedule: Schedule) {
        this.schedule = schedule;
    }

    migrateSchedule(oldSchedule: any): any {
        const newSchedule: any = {};

        for (const day in oldSchedule) {
            if (oldSchedule.hasOwnProperty(day)) {
                const value = oldSchedule[day];

                if (Array.isArray(value)) {
                    let [start, end, enable] = value;
                    if (!start)
                        start = '00:00';

                    if (!end)
                        end = '00:00';

                    if (enable == undefined)
                        enable = true;

                    newSchedule[day] = {
                        enable: enable,
                        times: [[start, end]]
                    };
                } else {
                    newSchedule[day] = value;
                }
            }
        }

        return newSchedule;
    }

    addRange(day: string): void {
        if (this.schedule[day]) {
            this.schedule[day].times.push(['00:00', '00:00']);
            //   this.cd.detectChanges();
        }
    }

    deleteRange(day: string): void {
        if (this.schedule[day]) {
            this.schedule[day].times.pop();
            //   this.cd.detectChanges();
        }
    }

    // ================== File Management ================== //
    onFileSelected(files: FileList | Event, type: 'picture' | 'logo' = 'picture') {
        let fileList: FileList | null;

        if (files instanceof Event) {
            const target = files.target as HTMLInputElement;
            fileList = target.files;
        } else {
            fileList = files;
        }

        if (fileList && fileList[0]) {
            const file = fileList[0];
            const requiredSize = 512;
            const reader = new FileReader();

            reader.readAsDataURL(file);
            reader.onload = (e: any) => {
                const img = new Image();
                img.src = e.target.result as string;

                img.onload = () => {
                    if (type == "picture" || (img.width === requiredSize && img.height === requiredSize)) {
                        this[`${type}Result`] = e.target.result;
                    } else {
                        const canvas = document.createElement('canvas');
                        const ctx: any = canvas.getContext('2d');

                        canvas.width = requiredSize;
                        canvas.height = requiredSize;

                        const scale = Math.min(requiredSize / img.width, requiredSize / img.height);
                        const newWidth = img.width * scale;
                        const newHeight = img.height * scale;
                        const offsetX = (requiredSize - newWidth) / 2;
                        const offsetY = (requiredSize - newHeight) / 2;

                        ctx.fillStyle = "#ffffff";
                        ctx.fillRect(0, 0, requiredSize, requiredSize);

                        ctx.drawImage(img, offsetX, offsetY, newWidth, newHeight);
                        const resizedBase64 = canvas.toDataURL('image/png');
                        this[`${type}Result`] = resizedBase64;
                    }
                };
            };

            reader.onerror = (error) => {
                console.error('Error loading file:', error);
            };
        }
    }

    onDeletePicture() {
        this.pictureResult = 'delete';
        this.project.picture = '';
    }

    onDeleteLogo() {
        this.logoResult = 'delete';
        this.project.logo = '';
    }

    // ================== Modality Management ================== //
    getSelectedModality() {
        return this.modalityOptions.find(opt => opt.value === this.project?.modality);
    }

    selectModality(value: string): void {
        this.project.modality = value;

        const query = {
            id: this.project.id,
            modality: value
        };

        this.projectService.setProject(query).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.pop.toast(this.translate.instant('settings.project.modality_updated'), "success");
                this.projectService.setCurrentProject(this.project);
            },
            error: (error: any) => {
                this.pop.toast(this.translate.instant('settings.project.modality_update_error', { errorCode: error.status }), "danger");
            }
        });

        this.modalityEditVisible = false;
    }

    toggleModalityEdit() {
        this.modalityEditVisible = !this.modalityEditVisible;

        if (this.modalityEditVisible && !this._modalityClickListener) {
            this._modalityClickListener = (event: MouseEvent) => {
                const target = event.target as HTMLElement;
                if (!target.closest('.modality-header')) {
                    this.modalityEditVisible = false;
                    document.removeEventListener('click', this._modalityClickListener!, true);
                    this._modalityClickListener = undefined;
                }
            };

            document.addEventListener('click', this._modalityClickListener, true);
        }
    }

    private _modalityClickListener?: (event: MouseEvent) => void;

    // ================== AI Modules Management ================== //
    onAiModulesChange(event: MatSelectChange) {
        let activeModules: string[] = event.value;
        this.labelProjects.forEach(labelProject => {
            // if unchecked, reset iterations
            if (!activeModules.includes(labelProject.name)) {
                delete this.selectedIterations[labelProject.name];
                // set iteration to 0 (if multiple iterations) when selecting a module
            } else if (!this.selectedIterations[labelProject.name] && labelProject.iterations && labelProject.iterations.length > 1)
                this.selectedIterations[labelProject.name] = labelProject.iterations[0] as string;
        });
    }

    convertIaModelFields(selectedModels: string[]): any {
        const allModels = ['crane', 'worker', 'risk', 'action', 'zone'];
        const result: any = {};

        allModels.forEach(model => {
            result[model] = selectedModels.includes(model);
        });

        return result;
    }

    selectIteration(projectName: string, iteration: string) {
        this.selectedIterations[projectName] = iteration;
    }

    iterationClick(event: Event, module: string) {
        // prevent unchecking of module when it is already checked
        if (this.selectedAIModules.includes(module))
            event.stopPropagation();
    }

    getSelectedModuleNames(): string {
        if (!this.selectedAIModules?.length) return '';

        return this.labelProjects
            .filter(p => this.selectedAIModules.includes(p.name))
            .map(p => p.displayName)
            .join(', ');
    }

    // ================== Archive Management ================== //
    archiveProject() {
        console.log('archiveProject called. Current archived status:', this.project.archived);
        let archived = !this.project.archived;

        this.pop.confirm(this.translate.instant(`projects.archive.${archived ? '' : 'undo_'}confirm`), (data: any) => {
            if (!data.dismissed)
                onConfirm()
        });

        const onConfirm = () => {
            let query = {
                id: this.project.id,
                archived: archived
            }

            this.projectService.setProject(query).pipe(takeUntil(this.destroy$)).subscribe({
                next: (res: any) => {
                  if (/document updated/i.test(res.message)) {
                    this.project.archived = archived;
                    if (this.projectSignal().id == this.project.id) {
                      this.projectService.setCurrentProject(this.project);
                    }

                    this.pop.toast(this.translate.instant(`projects.archive.${archived ? '' : 'undo_'}success`), 'success', 5000);
                  } else {
                    this.pop.toast(this.translate.instant(`projects.archive.${archived ? '' : 'undo_'}failure`), 'warning', 5000);
                  }
                },
                error: (err: any) => {
                  this.pop.toast(this.translate.instant(`projects.archive.${archived ? '' : 'undo_'}error`, { errorCode: err.status }), 'danger')
                }
            });
        }
    }

    // ================== Models ================== //
    private updateModelsOriginCoordinates(): void {
        // Snapshot : this.project est remplacé par setCurrentProject() juste après l'enregistrement
        const { id: projectId, lat, lon } = this.project;

        // Sans id la requête part en `projects=` (tous les modèles) ; sans coordonnées la mise à jour n'a pas de sens
        if (!projectId || !lat || !lon) return;

        // getModels() ne se termine jamais (updates socket) : chaque PUT réémet le flux, d'où le first() obligatoire
        this.modelService.getModels([projectId]).pipe(first(), takeUntil(this.destroy$)).subscribe((models: Model[]) => {
            const updateRequests: Observable<any>[] = [];

            (models ?? []).forEach((model: Model) => {
                const position = model.origin?.position;

                // Idempotent : lat/lon étant non nuls, un modèle mis à jour ne repasse plus cette condition
                if (!position || (position.mapLat && position.mapLon)) return;

                updateRequests.push(
                    this.modelService.updateModel({
                        id: model.id,
                        origin: { ...model.origin, position: { ...position, mapLat: lat, mapLon: lon } }
                    } as PutModel)
                );
            });

            if (updateRequests.length > 0) {
                forkJoin(updateRequests).pipe(takeUntil(this.destroy$)).subscribe();
            }
        });
    }

    // ================== Event Handlers ================== //
    onEndDateChanged(event: any) {
        this.project.endDate = event;
    }

    onReportTimeChanged(event: any) {
        this.project.nelia.reportTime = event;
    }

    // ================== Change Detection ================== //

    private saveInitialState() {
        this.initialState = {
            project: JSON.parse(JSON.stringify(this.project)),
            schedule: JSON.parse(JSON.stringify(this.schedule)),
            selectedAIModules: JSON.parse(JSON.stringify(this.selectedAIModules)),
            selectedIterations: JSON.parse(JSON.stringify(this.selectedIterations)),
            pictureResult: this.pictureResult,
            logoResult: this.logoResult,
            reportParams: this.riskReportParams,
        };

        // Snapshot of the project as persisted, for diffing edits against.
        this.currentProject = this.initialState.project;
    }

    private checkChangesLoop() {
        const now = performance.now();

        if (now - this.lastCheckTime >= this.CHECK_INTERVAL) {
            this.changeDetectionTrigger.update(v => v + 1);
            this.lastCheckTime = now;
        }

        this.animationFrameId = requestAnimationFrame(() => this.checkChangesLoop());
    }

    // ================== Helpers ================== //
    copy(content: string) {
        this.uiTools.copy(content);
    }
}
